import { useQueryClient } from '@tanstack/react-query';
import type { Map as MapboxMap } from 'mapbox-gl';
import { type RefObject, useCallback, useRef, useState } from 'react';
import { useMixpanel } from '@/hooks/useMixpanel';
import { placeVerificationKeyFactory } from '../services/placeVerification.api';
import {
  POI_MIXPANEL_EVENTS,
  type PoiInspectedPayload,
  poiPlacePayload,
} from '../utils/mixpanelEvents';
import {
  BASEMAP_POI_TARGET,
  mapMarkAt,
  type PointOfInterestPlace,
  placeAt,
  placeFromPointOfInterest,
} from '../utils/pointOfInterest';

function placeKey(place: PointOfInterestPlace): string {
  return `${place.name}:${place.longitude}:${place.latitude}`;
}

/**
 * Click a basemap place to open its card. The pointer shows it can be clicked.
 * `onOpened` lets the surface clear the deal or location already in that corner.
 */
export function usePointOfInterestInteractions(
  map: RefObject<MapboxMap | null>,
  suppressRef?: RefObject<boolean>,
  onOpened?: () => void,
) {
  const [selectedPlace, setSelectedPlace] = useState<PointOfInterestPlace | null>(null);
  const openKeyRef = useRef<string | null>(null);
  const onOpenedRef = useRef(onOpened);
  onOpenedRef.current = onOpened;
  const trackAction = useMixpanel((s) => s.trackAction);
  const queryClient = useQueryClient();

  const clearSelectedPlace = useCallback(() => {
    openKeyRef.current = null;
    setSelectedPlace(null);
  }, []);

  // Set on pointer-down and consumed by the click. The zone fill and the place
  // interaction both see one click: the first opens, the second must not toggle
  // it shut. The next pointer-down is a new click, so a second tap still closes.
  const handledClickRef = useRef<string | null>(null);

  const openPlace = useCallback(
    (place: PointOfInterestPlace) => {
      const key = placeKey(place);
      if (handledClickRef.current === key) return;
      handledClickRef.current = key;
      if (openKeyRef.current === key) {
        openKeyRef.current = null;
        setSelectedPlace(null);
        return;
      }
      openKeyRef.current = key;
      setSelectedPlace(place);
      onOpenedRef.current?.();
      const payload: PoiInspectedPayload = {
        ...poiPlacePayload(place),
        time: Date.now() / 1000,
      };
      // Share the card's lookup, but keep the opening even if its card closes before it settles.
      void queryClient
        .fetchQuery({
          ...placeVerificationKeyFactory.verify(place),
          staleTime: 0,
          retry: false,
        })
        .then(
          ({ verified }) =>
            trackAction(POI_MIXPANEL_EVENTS.INSPECTED, { ...payload, is_verified: verified }),
          () => trackAction(POI_MIXPANEL_EVENTS.INSPECTED, payload),
        );
    },
    [trackAction, queryClient],
  );

  function setupPointOfInterestInteractions() {
    const currentMap = map.current;
    if (!currentMap?.addInteraction) return () => {};

    const clickId = 'basemap-poi-click';
    const enterId = 'basemap-poi-enter';
    const leaveId = 'basemap-poi-leave';

    currentMap.addInteraction(clickId, {
      type: 'click',
      target: BASEMAP_POI_TARGET,
      handler(event) {
        if (suppressRef?.current) return;
        if (mapMarkAt(currentMap, event.point)) return;
        // The interaction hands over the first icon whose box contains the point.
        // A neighbor's title can be that icon, so the closest anchor wins.
        const place = placeAt(currentMap, event.point) ?? placeFromPointOfInterest(event.feature);
        if (!place) return;
        openPlace(place);
      },
    });
    currentMap.addInteraction(enterId, {
      type: 'mouseenter',
      target: BASEMAP_POI_TARGET,
      handler(event) {
        if (mapMarkAt(currentMap, event.point)) return;
        if (!placeFromPointOfInterest(event.feature)) return;
        currentMap.getCanvas().style.cursor = 'pointer';
      },
    });
    currentMap.addInteraction(leaveId, {
      type: 'mouseleave',
      target: BASEMAP_POI_TARGET,
      handler() {
        currentMap.getCanvas().style.cursor = '';
      },
    });

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      openKeyRef.current = null;
      setSelectedPlace(null);
    }
    function handlePointerDown() {
      handledClickRef.current = null;
    }
    window.addEventListener('keydown', handleKeyDown);
    currentMap.on('mousedown', handlePointerDown);
    currentMap.on('touchstart', handlePointerDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      currentMap.off('mousedown', handlePointerDown);
      currentMap.off('touchstart', handlePointerDown);
      currentMap.removeInteraction(clickId);
      currentMap.removeInteraction(enterId);
      currentMap.removeInteraction(leaveId);
      openKeyRef.current = null;
      setSelectedPlace(null);
    };
  }

  return { setupPointOfInterestInteractions, selectedPlace, clearSelectedPlace, openPlace };
}
